import {planWall, defaultPanels} from './lora-quantity-engine-v2.js?v=df3aad44643a';

import {materialColor} from './lora-material-color.js?v=cdc09f07fb75';

import {PLAN_FIELDS, normalizeModelSearch, validatePlanState, readPlanFragment, choosePlanSource, writeSavedPlan, clearSavedPlan, makePlanShareUrl} from './lora-planner-state.js?v=ba9dac0d7e9e';

const svgNS='http://www.w3.org/2000/svg';
class LoraQuantityPlanner extends HTMLElement {
  async connectedCallback(){
    if(this.ready)return;this.ready=true;
    this.config=JSON.parse(this.querySelector('[data-quantity-config]').textContent);
    this.t=this.config.strings;this.products=this.config.products.filter(p=>p.id);
    this.form=this.querySelector('form');this.abort=new AbortController();
    this.number=new Intl.NumberFormat(document.documentElement.lang||'he',{maximumFractionDigits:1});
    this.step=1;this.dimensionsAccepted=false;this.dimensionsAttempted=false;this.designVisited=false;this.selectedVariants=new Map();this.missingVariants=new Set();this.canSave=false;this.getStorage=()=>window.localStorage;
    this.querySelector('[data-catalog-status]').textContent=this.t.loading;
    try{
      const products=await this.loadCatalogue();
      if(this.abort.signal.aborted)return;
      const order=new Map(this.products.map((product,index)=>[product.handle,index]));
      this.products=products.sort((a,b)=>(order.get(a.handle)??100)-(order.get(b.handle)??100));
      this.querySelector('[data-catalog-status]').textContent='';
    }catch(error){
      if(this.abort.signal.aborted)return;
      this.querySelector('[data-catalog-status]').textContent=this.t.loadFallback;
    }
    for(const type of ['panel','slat']){
      const select=this.field(type);select.replaceChildren();
      for(const product of this.products.filter(p=>p.type===type)){
        const option=document.createElement('option');option.value=product.handle;
        option.textContent=this.shortLabel(product);select.append(option);
      }
    }
    this.initialValues=Object.fromEntries(PLAN_FIELDS.map(name=>[name,this.field(name).value]));
    this.resetPanelCount();this.adjustBand();
    const initial=choosePlanSource({hash:location.hash,search:location.search,getStorage:this.getStorage});
    this.invalidSharedState=initial.source==='invalid-share';
    if(initial.state){this.restorePlan(initial.state);this.designVisited=true;}
    if(initial.source==='model'){
      const product=this.products.find(p=>p.handle===initial.model);
      if(product){
        this.selectLinkedModel(product);
        const variant=product.variants?.find(v=>String(v.id)===initial.variant);
        if(variant)this.selectedVariants.set(product.handle,variant.id);
        else if(initial.variant)this.missingVariants.add(product.handle);
        this.resetPanelCount();this.adjustBand();
      }else if(initial.model)this.missingModel=initial.model;
    }
    if(!this.product('panel')||!this.product('slat')){this.querySelector('[data-status]').textContent=this.t.unavailable;return;}
    this.querySelector('fieldset[data-controls]').disabled=false;
    this.form.addEventListener('submit',e=>{e.preventDefault();this.goStep(this.step===1?2:3);},{signal:this.abort.signal});
    this.update=e=>{
      this.canSave=true;this.invalidSharedState=false;
      if(['width','panel'].includes(e.target.name))this.resetPanelCount();
      if(['height','panel','slat','mode'].includes(e.target.name))this.adjustBand();
      this.calculate();
    };
    // Native select pickers commit their selection with change.
    this.form.addEventListener('input',e=>{if(e.target.tagName!=='SELECT')this.update(e);},{signal:this.abort.signal});
    this.form.addEventListener('change',this.update,{signal:this.abort.signal});
    for(const button of this.querySelectorAll('[data-example]'))button.addEventListener('click',()=>this.example(button.dataset.example),{signal:this.abort.signal});
    this.setupJourney();this.setupStateActions();this.showStep(1,false);this.calculate();
    const notices={saved:'restoredPlan',share:'sharedPlan','invalid-share':'invalidShare',invalid:'invalidSaved',unavailable:'storageUnavailable'};
    if(notices[initial.source])this.stateNotice(notices[initial.source]);
    if(initial.source==='model')this.linkedModelNotice();
    // A shared plan opens at its result; merely visiting the homepage must not move the page.
    if(initial.source==='share')this.goStep(3,{restore:true});
    if(initial.source==='invalid-share')this.scrollIntoView({block:'start'});
  }
  /** Pair only an explicit model link; saved/shared plans and later manual choices stay intact. */
  selectLinkedModel(product){
    this.field(product.type).value=product.handle;
    const companionType=product.type==='panel'?'slat':'panel';
    const code=normalizeModelSearch(product.code||'');
    const candidates=code?this.products.filter(p=>p.type===companionType&&normalizeModelSearch(p.code||'')===code):[];
    // Prefer an available, calculable companion, then the closest height; keep catalogue order for ties.
    candidates.sort((a,b)=>Number(b.available===true)-Number(a.available===true)||Number(b.canPlan!==false)-Number(a.canPlan!==false)||Math.abs(a.height-product.height)-Math.abs(b.height-product.height));
    const companion=candidates[0];
    this.field('mode').value=companion?'mixed':product.type==='panel'?'panels':'slats';
    if(companion)this.field(companionType).value=companion.handle;
    this.linkedCompanionMissing=!companion;
  }
  linkedModelNotice(){
    if(!this.linkedCompanionMissing)return;
    const language=(document.documentElement.lang||'he').split('-')[0];
    const messages={
      he:'לא נמצא בקטלוג מוצר משלים עם אותו קוד דגם. המחשבון נפתח עם המוצר שבחרתם; אפשר לבחור שילוב ודגם נוסף ידנית.',
      en:'No companion with the same model code was found in the catalogue. The planner opened with your selected product; you can choose a mixed layout and another model manually.',
      ru:'В каталоге не найден дополнительный товар с тем же кодом модели. Открыт выбранный товар; можно вручную выбрать комбинацию и другую модель.'
    };
    const status=this.querySelector('[data-plan-status]');
    if(status)status.textContent=messages[language]||messages.he;
  }
  async loadCatalogue(){
    const controller=new AbortController(),cancel=()=>controller.abort();
    this.abort.signal.addEventListener('abort',cancel,{once:true});
    if(this.abort.signal.aborted)controller.abort();
    const timeout=setTimeout(cancel,8000);
    try{
      let url=new URL(this.dataset.root||'/',location.origin);url.searchParams.set('section_id','lora-planner-catalog');
      const products=new Map(),seen=new Set();
      while(url){
        if(url.origin!==location.origin||seen.has(url.href)||seen.size>=20)throw new Error('catalog-page');
        seen.add(url.href);const response=await fetch(url,{signal:controller.signal,credentials:'same-origin'});
        if(!response.ok)throw new Error('catalog-http');
        const doc=new DOMParser().parseFromString(await response.text(),'text/html');
        const element=doc.querySelector('[data-planner-catalog]');if(!element)throw new Error('catalog-empty');
        const data=JSON.parse(element.textContent);
        for(const product of data.products||[])if(product.id&&['panel','slat'].includes(product.type))products.set(product.id,product);
        if(data.next){url=new URL(data.next,location.origin);url.searchParams.set('section_id','lora-planner-catalog');}else url=null;
      }
      if(![...products.values()].some(p=>p.type==='panel')||![...products.values()].some(p=>p.type==='slat'))throw new Error('catalog-incomplete');
      return [...products.values()];
    }finally{clearTimeout(timeout);this.abort.signal.removeEventListener('abort',cancel);}
  }
  shortLabel(product){
    const parts=product.label.split('|').map(s=>s.trim());
    const useful=parts.filter(s=>s&&!/^\d+(?:[.,]\d+)?\s*[x×*]/i.test(s)&&!/^\d{2,6}(?:-\d+)?$/.test(s));
    return (useful.length>1?useful.slice(0,2).join(' · '):product.label).slice(0,115);
  }
  setupJourney(){
    const signal=this.abort.signal;
    this.drawingHome=this.querySelector('.quantity-planner__drawing')?.parentElement;
    if(typeof window.matchMedia==='function'){
      this.previewMedia=window.matchMedia('(max-width: 760px)');
      this.previewMedia.addEventListener('change',()=>this.placeDrawing(),{signal});
    }
    this.querySelector('[data-back]').addEventListener('click',()=>this.goStep(Math.max(1,this.step-1)),{signal});
    for(const button of this.querySelectorAll('[data-step-button]'))button.addEventListener('click',()=>this.goStep(Number(button.dataset.stepButton)),{signal});
    for(const button of this.querySelectorAll('[data-layout-choice]'))button.addEventListener('click',()=>{
      this.canSave=true;this.invalidSharedState=false;this.field('layout').value=button.dataset.layoutChoice;this.calculate();
    },{signal});
    for(const button of this.querySelectorAll('[data-mode-choice]'))button.addEventListener('click',()=>{
      this.field('mode').value=button.dataset.modeChoice;this.update({target:this.field('mode')});
    },{signal});
    const dialog=this.querySelector('[data-model-picker]');
    for(const button of this.querySelectorAll('[data-open-picker]'))button.addEventListener('click',()=>{
      this.pickerType=button.dataset.openPicker;this.pickerOpener=button;
      this.querySelector('[data-model-search]').value='';this.querySelector('[data-model-filter]').value='';
      this.renderPicker();dialog.showModal();this.querySelector('[data-model-search]').focus();
    },{signal});
    this.querySelector('[data-close-picker]').addEventListener('click',()=>dialog.close(),{signal});
    dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}},{signal});
    dialog.addEventListener('close',()=>this.pickerOpener?.focus(),{signal});
    this.querySelector('[data-model-search]').addEventListener('input',()=>this.renderPicker(),{signal});
    this.querySelector('[data-model-filter]').addEventListener('change',()=>this.renderPicker(),{signal});
    this.querySelector('[data-model-grid]').addEventListener('click',event=>{
      const button=event.target.closest('[data-model-handle]');if(!button)return;
      this.field(this.pickerType).value=button.dataset.modelHandle;this.missingModel=null;this.missingVariants.delete(button.dataset.modelHandle);
      this.update({target:this.field(this.pickerType)});dialog.close();
    },{signal});
  }
  renderPicker(){
    const query=normalizeModelSearch(this.querySelector('[data-model-search]').value),finish=this.querySelector('[data-model-filter]').value;
    const products=this.products.filter(p=>p.type===this.pickerType&&(!finish||p.finish===finish)&&(!query||normalizeModelSearch(`${p.label} ${p.code||''} ${p.nominalWidth||p.width} ${p.width} ${p.height}`).includes(query)));
    const grid=this.querySelector('[data-model-grid]');grid.replaceChildren();
    this.querySelector('[data-model-count]').textContent=products.length?`${products.length} ${this.t.modelCount}`:this.t.noModels;
    const fragment=document.createDocumentFragment();
    for(const product of products){
      const button=document.createElement('button');button.type='button';button.className='quantity-planner__model-card';button.dataset.modelHandle=product.handle;
      button.setAttribute('aria-pressed',String(this.field(this.pickerType).value===product.handle));
      if(product.image){const image=document.createElement('img');image.src=product.image;image.alt='';image.width=160;image.height=160;image.loading='lazy';button.append(image);}
      const title=document.createElement('strong');title.textContent=this.shortLabel(product);button.append(title);
      if(product.code){const code=document.createElement('span');code.textContent=`${this.t.model} ${product.code}`;button.append(code);}
      const size=document.createElement('small');size.textContent=product.canPlan===false?this.t.reviewBadge:`${this.t.coverage}: ${this.fmt(product.width)} ${this.t.cm} · ${this.fmt(product.height)} ${this.t.cm}`;button.append(size);
      if(product.available===false){const stock=document.createElement('small');stock.textContent=this.t.unavailableModel;button.append(stock);}
      fragment.append(button);
    }grid.append(fragment);
  }
  /** Move through dimensions, layout, then finishes without replacing form state. */
  goStep(next,{restore=false}={}){
    next=Math.max(1,Math.min(3,Number(next)||1));
    if(next===1){
      this.dimensionsAccepted=false;this.dimensionsAttempted=false;
      this.showStep(1,false);this.calculate();this.focusStep();return;
    }
    this.dimensionsAttempted=true;this.clearInputErrors();
    try{this.validateInputs({dimensionsOnly:true});}
    catch(error){
      this.dimensionsAccepted=false;this.showStep(1,false);this.calculate();
      this.showInputError(error);this.field(error.inputName)?.focus();return;
    }
    // The progress navigation follows the same gate as the Continue button.
    if(next===3&&!this.designVisited&&!restore)next=2;
    this.dimensionsAccepted=true;this.designVisited=true;
    this.showStep(next,false);this.calculate();
    if(next===3&&!this.plan&&!this.reviewing){
      const invalid=this.querySelector('[aria-invalid="true"]');
      const owner=invalid?.closest('[data-step-panel]');
      const errorStep=Number(owner?.dataset.stepPanel)||2;
      this.showStep(errorStep,false);
      invalid?.closest('details')?.setAttribute('open','');
      if(invalid){invalid.focus();invalid.scrollIntoView({block:'center'});return;}
    }
    this.focusStep();
  }
  showStep(next,focus=true){
    this.step=next;this.dataset.step=String(next);
    for(const panel of this.querySelectorAll('[data-step-panel]'))panel.hidden=Number(panel.dataset.stepPanel)!==next;
    for(const button of this.querySelectorAll('[data-step-button]')){
      if(Number(button.dataset.stepButton)===next)button.setAttribute('aria-current','step');
      else button.removeAttribute('aria-current');
    }
    this.placeDrawing();
    this.querySelector('.quantity-planner__preview').hidden=next===1;
    this.querySelector('[data-back]').hidden=next===1;
    const submit=this.querySelector('[data-next]');submit.hidden=next===3;
    submit.textContent=next===1?(this.t.wizardNextDesign||this.t.next):(this.t.wizardNextSummary||this.t.show);
    if(focus)this.focusStep();
  }
  /** Keep one accessible drawing close to the mobile layout controls. */
  placeDrawing(){
    const drawing=this.querySelector('.quantity-planner__drawing'),slot=this.querySelector('[data-mobile-preview]');
    if(!drawing||!slot||!this.drawingHome)return;
    if(this.step===2&&this.previewMedia?.matches){if(drawing.parentElement!==slot)slot.append(drawing);}
    else if(drawing.parentElement!==this.drawingHome)this.drawingHome.prepend(drawing);
  }
  focusStep(){
    const heading=this.querySelector(`[data-step-panel="${this.step}"] h3`);
    heading?.setAttribute('tabindex','-1');heading?.focus({preventScroll:true});
    this.querySelector('.quantity-planner__steps')?.scrollIntoView({block:'start',behavior:'instant'});
  }
  updateDimensionLabels(){
    for(const name of ['width','height']){
      const raw=this.field(name).value,value=Number(raw);
      const label=raw&&Number.isFinite(value)&&value>0?`${this.fmt(value)} ${this.t.cm}`:this.t[name];
      for(const item of this.querySelectorAll(`[data-dimension-${name}]`))item.textContent=label;
    }
    const width=this.field('width').value,height=this.field('height').value;
    for(const item of this.querySelectorAll('[data-dimension-summary]'))item.textContent=width&&height?`${this.t.wall}: ${this.fmt(Number(width))} × ${this.fmt(Number(height))} ${this.t.cm}`:'';
  }
  showInputError(error){
    const status=this.querySelector('[data-status]');
    status.textContent=error.userMessage||this.t.errors[error.message]||this.t.unavailable;
    status.classList.add('quantity-planner__error');
    const name=error.inputName||({'band-size':'band','kerf-size':'kerf','panel-count':'panelCount','two-panels':'panelCount'}[error.message]);
    if(name){
      const input=this.field(name);input.setAttribute('aria-invalid','true');
      const descriptions=(input.getAttribute('aria-describedby')||'').split(/\s+/).filter(Boolean);
      if(status.id)input.setAttribute('aria-describedby',[...new Set([...descriptions,status.id])].join(' '));
    }
  }
  renderModels(){
    for(const button of this.querySelectorAll('[data-mode-choice]'))button.setAttribute('aria-pressed',String(button.dataset.modeChoice===this.field('mode').value));
    for(const type of ['panel','slat']){
      const product=this.product(type);if(!product)continue;
      const image=this.querySelector(`[data-selected-image="${type}"]`);image.hidden=!product.image;if(product.image)image.src=product.image;
      this.querySelector(`[data-selected-title="${type}"]`).textContent=[product.code,this.shortLabel(product)].filter(Boolean).join(' · ');
      this.querySelector(`[data-selected-size="${type}"]`).textContent=product.canPlan===false?this.t.reviewBadge:`${this.t.coverage}: ${this.fmt(product.width)} ${this.t.cm} · ${this.t.nominal}: ${this.fmt(product.nominalWidth||product.width)} × ${this.fmt(product.height)} ${this.t.cm}`;
    }
    for(const button of this.querySelectorAll('[data-layout-choice]'))button.setAttribute('aria-pressed',String(button.dataset.layoutChoice===this.field('layout').value));
  }
  previewState(conceptual){
    this.querySelector('.quantity-planner__drawing').hidden=false;
    this.querySelector('.quantity-planner__legend').hidden=conceptual;
    if(conceptual)this.querySelector('[data-announcement]').textContent='';
    const title=this.querySelector('[data-drawing-title]'),note=this.querySelector('[data-drawing-note]');
    if(title)title.textContent=conceptual?(this.t.conceptTitle||this.t.reviewBadge):this.t.drawing;
    if(note)note.textContent=conceptual?(this.t.conceptNote||this.t.needsReview):this.t.drawingNote;
  }
  paintMaterials(){
    for(const type of ['panel','slat']){
      const selector=`[data-material="${type}"]`,shapes=this.querySelectorAll(selector);
      if(!shapes.length)continue;
      const product=this.missingModel?null:this.product(type),source=product?.image;
      const apply=color=>{
        for(const shape of this.querySelectorAll(selector))shape.setAttribute('fill',color);
        const swatch=this.querySelector(`[data-swatch="${type}"]`);
        if(swatch)swatch.style.setProperty('--qp-material-color',color);
      };
      apply(type==='panel'?'#d9d2c5':'#ac9071');
      materialColor(source).then(color=>{
        if(!color||this.abort?.signal.aborted||this.missingModel||this.product(type)?.image!==source)return;
        apply(color);
      });
    }
  }
  renderPreviewMaterials(products){
    const host=this.querySelector('[data-preview-materials]');if(!host)return;
    host.replaceChildren();host.setAttribute('role','group');host.setAttribute('aria-label',this.t.selectedMaterials||this.t.models);
    for(const product of products.filter(Boolean)){
      const item=document.createElement('a');item.className='quantity-planner__material';item.href=product.url;
      if(product.image){const image=document.createElement('img');image.src=product.image;image.alt='';image.width=64;image.height=64;item.append(image);}
      const label=document.createElement('span');label.textContent=[product.code,this.shortLabel(product)].filter(Boolean).join(' · ');item.append(label);host.append(item);
    }
  }
  drawConcept(){
    // This preview describes the arrangement, never unverified product dimensions or purchase quantities.
    const svg=this.querySelector('svg[data-wall]');svg.replaceChildren();
    svg.setAttribute('viewBox','0 0 360 250');svg.setAttribute('aria-label',this.t.conceptTitle||this.t.reviewBadge);
    const mode=this.field('mode').value,layout=this.field('layout').value;
    const arrangements={'slats-sides':['s','s','p','p','s','s'],'panels-left':['p','p','s','s','s','s'],'panels-right':['s','s','s','s','p','p'],'panels-sides':['p','s','s','s','s','p'],'alternating':['p','s','s','p','s','s']};
    const blocks=mode==='panels'?['p','p','p']:mode==='slats'?Array(12).fill('s'):arrangements[layout]||arrangements['slats-sides'];
    const weight=kind=>kind==='p'?4:1,total=blocks.reduce((sum,kind)=>sum+weight(kind),0);
    const group=this.node('g',{transform:'translate(10 10)'});svg.append(group);let at=0;
    for(const kind of blocks){
      const width=340*weight(kind)/total;
      group.append(this.node('rect',{x:at,y:0,width,height:225,'data-material':kind==='p'?'panel':'slat',fill:kind==='p'?'#d9d2c5':'#ac9071',stroke:'#00000030','stroke-width':1}));
      if(kind==='s')for(let i=1;i<4;i++)group.append(this.node('line',{x1:at+width*i/4,x2:at+width*i/4,y1:0,y2:225,stroke:'#00000040','stroke-width':.7}));
      at+=width;
    }
    this.previewState(true);this.querySelector('[data-completion-legend]').hidden=true;this.paintMaterials();
  }
  reviewModel(products){
    this.querySelector('[data-form-summary]').textContent='';
    this.plan=null;this.reviewing=true;this.querySelector('[data-results]').hidden=true;this.querySelector('[data-empty]').hidden=true;
    this.drawConcept();this.querySelector('[data-preview-summary]').textContent=this.t.reviewBadge;this.querySelector('[data-completion-legend]').hidden=true;
    this.querySelector('[data-status]').textContent='';this.querySelector('[data-whatsapp]').removeAttribute('href');
    this.querySelector('[data-review]').hidden=false;this.querySelector('[data-review-text]').textContent=this.missingModel?this.t.modelMissing:products.some(p=>this.missingVariants?.has(p?.handle))?this.t.variantReview:this.t.needsReview;
    const lines=[this.t.quote,`${this.t.wall}: ${this.field('width').value} × ${this.field('height').value} ${this.t.cm}`];
    if(this.missingModel)lines.push(new URL(`${this.dataset.root||'/'}products/${encodeURIComponent(this.missingModel)}`,location.origin).href);
    else for(const product of products){const modelURL=new URL(product.url,location.origin);const variant=this.selectedVariants.get(product.handle)||product.variantId;if(variant&&!this.missingVariants?.has(product.handle))modelURL.searchParams.set('variant',variant);lines.push(product.label,modelURL.href);}
    lines.push(this.t.quoteCheck);const url=new URL('https://wa.me/972509445045');url.searchParams.set('text',lines.join('\n'));this.querySelector('[data-review-whatsapp]').href=url.href;
  }
  disconnectedCallback(){this.abort?.abort();this.ready=false;}
  field(name){return this.form.elements.namedItem(name);}
  product(type){return this.products.find(p=>p.type===type&&p.handle===this.field(type).value);}
  fmt(value){return this.number.format(value);}
  resetPanelCount(){
    const width=Number(this.field('width').value),p=this.product('panel');if(!p||width<=0||p.width<=0)return;
    this.field('panelCount').max=Math.ceil(width/p.width);this.field('panelCount').value=defaultPanels(width,p.width);
  }
  adjustBand(){
    const mode=this.field('mode').value,p=this.product('panel'),s=this.product('slat');if(!p||!s)return;
    const minimum=Math.max(0,Number(this.field('height').value)-Math.min(...[...(mode!=='slats'?[p.height]:[]),...(mode!=='panels'?[s.height]:[])]));
    this.field('band').min=minimum;this.field('band').value=Math.max(minimum,Number(this.field('band').value)||20);
  }
  example(kind){
    this.canSave=true;this.invalidSharedState=false;this.missingModel=null;this.missingVariants.clear();this.field('panel').selectedIndex=0;this.field('slat').selectedIndex=0;
    this.field('width').value=kind==='240'?240:300;this.field('height').value=kind==='height'?300:280;
    this.field('mode').value=kind==='240'?'panels':'mixed';this.field('layout').value='slats-sides';
    this.field('reserve').value='0';this.field('band').value=20;this.field('bandPosition').value='bottom';
    this.field('leftEnd').value='internal';this.field('rightEnd').value='internal';this.resetPanelCount();this.adjustBand();this.goStep(2);
  }
  calculate(){
    this.clearInputErrors();this.updateStateActions(false);this.reviewing=false;this.updateDimensionLabels();
    // A fresh or reopened dimensions screen has no calculated result or quote.
    if(!this.dimensionsAccepted){
      this.plan=null;this.querySelector('[data-results]').hidden=true;this.querySelector('[data-empty]').hidden=true;
      this.querySelector('.quantity-planner__preview').hidden=true;
      this.querySelector('[data-review]').hidden=true;
      this.querySelector('[data-whatsapp]').removeAttribute('href');this.querySelector('[data-review-whatsapp]').removeAttribute('href');
      for(const selector of ['[data-announcement]','[data-form-summary]','[data-preview-summary]'])this.querySelector(selector).textContent='';
      if(this.dimensionsAttempted){try{this.validateInputs({dimensionsOnly:true});}catch(error){this.showInputError(error);}}
      return;
    }
    this.querySelector('[data-review]').hidden=true;this.querySelector('[data-review-whatsapp]').removeAttribute('href');
    const mode=this.field('mode').value,layout=this.field('layout').value;
    this.renderModels();this.querySelector('[data-mixed-advanced]').hidden=mode!=='mixed';
    if(mode==='panels'&&this.field('reserve').value==='2')this.field('reserve').value='0';
    this.field('reserve').querySelector('option[value="2"]').disabled=mode==='panels';
    this.querySelector('[data-mixed]').hidden=mode!=='mixed';
    this.querySelector('[data-panel-model]').hidden=mode==='slats';
    this.querySelector('[data-slat-model]').hidden=mode==='panels'&&this.field('reserve').value!=='1';
    const panel=this.product('panel'),slat=this.product('slat');
    const active=[...(mode!=='slats'?[panel]:[]),...(mode!=='panels'||this.field('reserve').value==='1'?[slat]:[])];
    const needsReview=this.missingModel||active.some(product=>!product||product.canPlan===false||this.missingVariants?.has(product.handle));
    this.renderPreviewMaterials(this.missingModel?[]:active);
    const height=Number(this.field('height').value),width=Number(this.field('width').value);
    const minimumBand=needsReview?0:Math.max(0,height-Math.min(...[...(mode!=='slats'?[panel.height]:[]),...(mode!=='panels'?[slat.height]:[])]));
    this.querySelector('[data-completion]').hidden=needsReview||minimumBand===0;
    this.querySelector('[data-minimum-band]').textContent=`${this.t.minimumBand} ${this.fmt(minimumBand)} ${this.t.cm}`;
    try{
      if(this.invalidSharedState){const error=new Error('invalid-share');error.userMessage=this.t.invalidShare;throw error;}
      this.validateInputs({includeBand:!this.querySelector('[data-completion]').hidden,includePanelCount:mode==='mixed'&&!needsReview});
      if(needsReview){this.reviewModel(active);return;}
      const config={width,height,panel,slat,mode,layout,panelCount:Number(this.field('panelCount').value),reserve:this.field('reserve').value,
        band:Number(this.field('band').value),bandPosition:this.field('bandPosition').value,leftEnd:this.field('leftEnd').value,rightEnd:this.field('rightEnd').value,
        profileLength:this.config.profileLength,kerfMm:Number(this.field('kerf').value)};
      this.plan=planWall(config);this.previewState(false);
      this.querySelector('[data-results]').hidden=false;this.querySelector('[data-empty]').hidden=true;
      this.draw(this.plan);this.results(this.plan,panel,slat);this.updateStateActions(true);
    }catch(error){
      this.plan=null;this.querySelector('[data-results]').hidden=true;this.querySelector('[data-empty]').hidden=true;
      this.drawConcept();this.querySelector('[data-preview-summary]').textContent='';
      this.querySelector('[data-completion-legend]').hidden=true;
      this.querySelector('[data-form-summary]').textContent='';this.querySelector('[data-announcement]').textContent='';
      this.showInputError(error);
      this.querySelector('[data-whatsapp]').removeAttribute('href');
    }
  }
  clearInputErrors(){
    for(const name of ['width','height','kerf','band','panelCount']){
      const input=this.field(name);input.removeAttribute('aria-invalid');
      const errorId=this.querySelector('[data-status]').id;
      const descriptions=(input.getAttribute('aria-describedby')||'').split(/\s+/).filter(id=>id&&id!==errorId);
      if(descriptions.length)input.setAttribute('aria-describedby',descriptions.join(' '));else input.removeAttribute('aria-describedby');
    }
    const status=this.querySelector('[data-status]');status.textContent='';status.classList.remove('quantity-planner__error');
  }
  validateInputs({includeBand,includePanelCount,dimensionsOnly=false}={}){
    const rules={width:[30,1200,10,'wall-size'],height:[30,1200,10,'wall-size']};
    if(!dimensionsOnly)rules.kerf=[0,6,10,'kerf-size'];
    if(includeBand)rules.band=[Number(this.field('band').min)||0,1200,10,'band-size'];
    if(includePanelCount)rules.panelCount=[1,Number(this.field('panelCount').max)||120,1,'panel-count'];
    for(const [name,[min,max,scale,code]] of Object.entries(rules)){
      const raw=this.field(name).value.trim(),value=Number(raw),label=this.t[name]||name;
      if(raw===''||!Number.isFinite(value)||value<min||value>max||Math.abs(value*scale-Math.round(value*scale))>0.000001){
        const error=new Error(code);error.inputName=name;
        error.userMessage=raw===''?(this.t.requiredField||'{field}').replace('{field}',label):`${label}: ${this.t.errors[code]||this.t.unavailable}`;
        throw error;
      }
    }
  }
  stateNotice(key){const status=this.querySelector('[data-plan-status]');if(status)status.textContent=this.t[key]||'';}
  restorePlan(state){
    for(const name of PLAN_FIELDS){
      if(['panel','slat'].includes(name)){
        if(this.products.some(p=>p.type===name&&p.handle===state[name]))this.field(name).value=state[name];
        else this.missingModel=state[name];
      }else this.field(name).value=name==='reserve'&&state[name]==='auto'?'0':state[name];
    }
    // Recompute limits from current data, but preserve the customer's chosen count and completion.
    const chosenCount=this.field('panelCount').value,chosenBand=this.field('band').value;
    this.resetPanelCount();this.adjustBand();this.field('panelCount').value=chosenCount;this.field('band').value=chosenBand;
    for(const type of ['panel','slat']){
      const product=this.product(type),variant=product?.variants?.find(v=>String(v.id)===state[`${type}Variant`]);
      if(variant)this.selectedVariants.set(product.handle,variant.id);
      else if(product&&state[`${type}Variant`])this.missingVariants.add(product.handle);
    }
  }
  planState(){
    const state={v:1,...Object.fromEntries(PLAN_FIELDS.map(name=>[name,this.field(name).value]))};
    for(const name of ['width','height','panelCount','band','kerf'])if(state[name].trim()!==''&&Number.isFinite(Number(state[name])))state[name]=String(Number(state[name]));
    // Inactive controls may retain an empty edit from a previous mode. They must not block sharing a valid plan.
    if(state.mode!=='mixed'&&(!/^\d+$/.test(state.panelCount)||Number(state.panelCount)<1||Number(state.panelCount)>120))state.panelCount='1';
    if(this.querySelector('[data-completion]').hidden&&(!/^\d+(?:\.\d)?$/.test(state.band)||Number(state.band)>1200))state.band='20';
    for(const type of ['panel','slat']){
      const product=this.product(type),chosen=this.selectedVariants.get(product?.handle)||product?.variantId;
      if(product?.variants?.some(v=>String(v.id)===String(chosen)))state[`${type}Variant`]=String(chosen);
    }
    return validatePlanState(state);
  }
  setupStateActions(){
    const signal=this.abort.signal;
    window.addEventListener('hashchange',()=>this.openSharedFragment(),{signal});
    this.querySelector('[data-share-plan]')?.addEventListener('click',()=>this.sharePlan(),{signal});
    const reset=this.querySelector('[data-reset-plan]');if(reset){reset.disabled=false;reset.addEventListener('click',()=>this.resetPlan(),{signal});}
  }
  openSharedFragment(){
    const fragment=readPlanFragment(location.hash);if(!fragment.present)return;
    this.canSave=false;this.missingModel=null;this.missingVariants.clear();this.selectedVariants.clear();
    this.invalidSharedState=!fragment.state;
    if(fragment.state)this.restorePlan(fragment.state);
    this.designVisited=Boolean(fragment.state);
    this.goStep(fragment.state?3:1,{restore:true});
    this.stateNotice(fragment.state?'sharedPlan':'invalidShare');
  }
  updateStateActions(valid){
    const share=this.querySelector('[data-share-plan]');if(share)share.disabled=!valid;
    const field=this.querySelector('[data-plan-link]');if(field)field.value='';
    const area=this.querySelector('[data-plan-link-wrap]');if(area)area.hidden=true;
    if(!valid||!this.canSave)return;
    const state=this.planState();if(!state)return;
    this.stateNotice(writeSavedPlan(this.getStorage,state)?'savedPlan':'storageUnavailable');
  }
  async sharePlan(){
    if(!this.plan)return;
    const url=makePlanShareUrl(this.planState(),location.origin,this.dataset.root||'/');if(!url)return;
    const area=this.querySelector('[data-plan-link-wrap]'),field=this.querySelector('[data-plan-link]');
    area.hidden=false;field.value=url;
    try{await navigator.clipboard.writeText(url);this.stateNotice('copiedPlan');}
    catch{this.stateNotice('copyPlanFallback');field.focus();field.select();}
  }
  resetPlan(){
    this.canSave=false;this.invalidSharedState=false;this.missingModel=null;this.selectedVariants.clear();this.missingVariants.clear();
    const removed=clearSavedPlan(this.getStorage);let linkCleared=true;
    // Clear only our shared-plan fragment. Preserve the path, product entry query and campaign parameters verbatim.
    if(readPlanFragment(location.hash).present){
      try{history.replaceState(history.state,'',`${location.href.split('#')[0]}#quantity-planner`);}
      catch{linkCleared=false;}
    }
    for(const name of PLAN_FIELDS)this.field(name).value=this.initialValues[name];
    this.dimensionsAccepted=false;this.dimensionsAttempted=false;this.designVisited=false;
    this.resetPanelCount();this.adjustBand();this.goStep(1);
    this.stateNotice(!linkCleared?'clearLinkUnavailable':removed?'clearedPlan':'clearUnavailable');
  }
  node(tag,attrs,text){const el=document.createElementNS(svgNS,tag);for(const [key,value] of Object.entries(attrs))el.setAttribute(key,value);if(text!==undefined)el.textContent=text;return el;}
  draw(plan){
    const svg=this.querySelector('svg[data-wall]');svg.replaceChildren();
    const w=plan.width,h=plan.height,font=Math.max(12,w/24),pad=Math.max(36,font*2.4);
    svg.setAttribute('viewBox',`0 0 ${w+pad*2} ${h+pad*2}`);
    svg.setAttribute('aria-label',`${this.t.drawing} ${this.fmt(w)} × ${this.fmt(h)} ${this.t.cm}. ${this.t.verticalJoins}: ${plan.verticalJoints}.`);
    const g=this.node('g',{transform:`translate(${pad} ${pad})`});svg.append(g);
    for(const column of plan.columns){
      g.append(this.node('rect',{x:column.start,y:0,width:column.width,height:h,'data-material':column.kind,fill:column.kind==='panel'?'#d9d2c5':'#ac9071',stroke:'#00000030','stroke-width':.6}));
      if(column.kind==='slat')for(let i=1;i<4;i++)g.append(this.node('line',{x1:column.start+column.width*i/4,x2:column.start+column.width*i/4,y1:0,y2:h,stroke:'#00000040','stroke-width':.7}));
    }
    const lineWidth=Math.max(1.5,w/180);
    const profileLine=attrs=>{
      g.append(this.node('line',{...attrs,stroke:'#ffffff','stroke-opacity':.7,'stroke-width':attrs['stroke-width']+1.6}));
      g.append(this.node('line',attrs));
    };
    this.querySelector('[data-completion-legend]').hidden=!plan.band||!plan.slats.mainUnits;
    for(const x of plan.jointPositions)profileLine({x1:x,x2:x,y1:0,y2:h,stroke:'#203f35','stroke-width':lineWidth});
    if(plan.band){
      const y=plan.bandPosition==='bottom'?h-plan.band:plan.band;
      for(const run of plan.profiles.horizontalRuns)profileLine({x1:run.start,x2:run.start+run.width,y1:y,y2:y,stroke:'#203f35','stroke-width':lineWidth,'data-horizontal-profile':''});
      for(const column of plan.columns.filter(c=>c.kind==='slat'))g.append(this.node('line',{x1:column.start,x2:column.start+column.width,y1:y,y2:y,stroke:'#806b52','stroke-width':Math.max(.6,lineWidth*.35),'stroke-dasharray':`${font*.28} ${font*.16}`,'data-slat-completion':''}));
      const labelRun=plan.profiles.horizontalRuns.reduce((longest,run)=>!longest||run.width>longest.width?run:longest,null);
      const labelX=labelRun?labelRun.start+labelRun.width/2:w/2;
      const marker=this.node('g',{});marker.append(this.node('rect',{x:labelX-font*2,y:y-font*.72,width:font*4,height:font*1.44,fill:'#fff'}));
      marker.append(this.node('text',{x:labelX,y:y+font*.34,'text-anchor':'middle',fill:'#203f35','font-size':font*.8},`${this.fmt(plan.band)} ${this.t.cm}`));g.append(marker);
    }
    for(const [side,x] of [['left',0],['right',w]])profileLine({x1:x,x2:x,y1:0,y2:h,stroke:plan.ends[side]==='external'?'#9d753b':'#203f35','stroke-width':lineWidth*1.6});
    g.append(this.node('text',{x:w/2,y:-font*.8,'text-anchor':'middle',fill:'#203f35','font-size':font},`${this.fmt(w)} ${this.t.cm}`));
    g.append(this.node('text',{x:w+font*.7,y:h/2,transform:`rotate(90 ${w+font*.7} ${h/2})`,'text-anchor':'middle',fill:'#203f35','font-size':font*.9},`${this.fmt(h)} ${this.t.cm}`));
    let group=[];
    const labelGroup=()=>{if(!group.length)return;const total=group.reduce((a,c)=>a+c.width,0),start=group[0].start;if(total>font*2)g.append(this.node('text',{x:start+total/2,y:h+font*1.6,'text-anchor':'middle',fill:'#536258','font-size':font*.8},this.fmt(total)));group=[];};
    plan.columns.forEach(c=>{if(c.kind==='panel'){labelGroup();group=[c];labelGroup();}else group.push(c);});labelGroup();this.paintMaterials();
  }
  results(plan,panel,slat){
    const quantity={panels:plan.panels.quantity,slats:plan.slats.quantity,connectors:plan.profiles.connectors.length,finishes:plan.profiles.finishes.length,corners:plan.profiles.corners.length};
    this.querySelector('[data-preview-summary]').textContent=['panels','slats','connectors'].filter(key=>quantity[key]).map(key=>`${this.fmt(quantity[key])} ${this.t[key]}`).join(' · ');
    for(const [key,value] of Object.entries(quantity)){this.querySelector(`[data-count-${key}]`).textContent=this.fmt(value);this.querySelector(`[data-row-${key}]`).hidden=value===0;}
    for(const [type,material] of [['panel',plan.panels],['slat',plan.slats]]){
      this.querySelector(`[data-${type}-breakdown]`).textContent=[`${material.mainUnits} ${this.t.mainUnits}`,...(material.completionUnits?[`${material.completionUnits} ${this.t.completionUnits}`]:[]),...(material.spareUnits?[`${material.spareUnits} ${this.t.reserveUnits}`]:[])].join(' + ');
    }
    const widths=new Map();
    for(const column of plan.columns){const key=`${column.kind}:${column.width}`;const group=widths.get(key)||{width:column.width,count:0};group.count++;widths.set(key,group);}
    this.querySelector('[data-width-info]').textContent=`${[...widths.values()].map(group=>`${group.count} × ${this.fmt(group.width)}`).join(' + ')} = ${this.fmt(plan.width)} ${this.t.cm}`;
    this.querySelector('[data-form-summary]').textContent=`${this.fmt(plan.width)} × ${this.fmt(plan.height)} ${this.t.cm} · ${this.field('mode').selectedOptions[0].textContent}`;
    this.querySelector('[data-profile-splice]').hidden=!plan.profiles.verticalSplice;this.querySelector('[data-profile-splice]').textContent=this.t.profileSplice;
    this.querySelector('[data-panel-name]').textContent=panel.label;this.querySelector('[data-panel-name]').href=panel.url;
    this.querySelector('[data-slat-name]').textContent=slat.label;this.querySelector('[data-slat-name]').href=slat.url;
    this.querySelector('[data-joint-info]').textContent=`${plan.verticalJoints} ${this.t.verticalJoins}${plan.profiles.horizontalLengths.length?` + ${this.t.horizontalJoin}`:''}`;
    const completionRule=[...(plan.panels.mainUnits?[this.t.horizontalPanels]:[]),...(plan.slats.mainUnits?[this.t.slatCompletion]:[])].join(' ');
    this.querySelector('[data-completion-info]').textContent=plan.band?`${this.t.completionInfo} ${this.fmt(plan.band)} ${this.t.cm} · ${plan.bandPosition==='bottom'?this.t.fromFloor:this.t.fromCeiling}. ${completionRule}`:this.t.fullHeight;
    const trimText=plan.columns.some(c=>c.kind==='panel'&&c.width<panel.width)?this.t.panelsTrimmed:this.t.exactPanels;
    this.querySelector('[data-extra-info]').textContent=plan.mode==='mixed'?`${this.t.extraIncluded} ${plan.extraSlats}. ${trimText}`:plan.spareSlats?`${this.t.spareInfo} ${plan.spareSlats}`:this.t.exactPanels;
    this.querySelector('[data-aluminium-info]').textContent=`${this.t.stockLength} ${this.fmt(plan.profileLength)} ${this.t.cm}`;
    this.querySelector('[data-cutting-note]').textContent=`${this.t.kerfNote} ${this.fmt(plan.kerfMm)} ${this.t.mm}. ${this.t.cutNote}`;
    const host=this.querySelector('[data-cutting]');host.replaceChildren();
    for(const [key,units] of Object.entries(plan.cutting)){
      if(!units.length)continue;const heading=document.createElement('h4');heading.textContent=this.t[key];host.append(heading);
      const list=document.createElement('ul');
      const grouped=new Map();
      for(const unit of units){const text=unit.map(c=>typeof c==='number'?`${this.fmt(c)} ${this.t.cm}`:`${this.fmt(c.width)} × ${this.fmt(c.height)} ${this.t.cm}`).join(' + ');grouped.set(text,(grouped.get(text)||0)+1);}
      for(const [cut,count] of grouped){const li=document.createElement('li');const amount=document.createElement('strong');amount.textContent=`${count} ${this.t.units}: `;const content=document.createElement('bdi');content.dir='ltr';content.textContent=cut;li.append(amount,content);list.append(li);}host.append(list);
    }
    const layoutText=this.field('layout').selectedOptions[0].textContent;
    const lines=[this.t.quote,`${this.t.wall}: ${this.fmt(plan.width)} × ${this.fmt(plan.height)} ${this.t.cm}`,this.field('mode').selectedOptions[0].textContent];
    if(plan.mode==='mixed')lines.push(layoutText);
    if(plan.panels.quantity)lines.push(`${panel.label}: ${plan.panels.quantity} ${this.t.units}`);
    if(plan.slats.quantity)lines.push(`${slat.label}: ${plan.slats.quantity} ${this.t.units}`);
    for(const key of ['connectors','finishes','corners'])if(quantity[key])lines.push(`${this.t[key]}: ${quantity[key]} ${this.t.units}`);
    lines.push(`${this.t.verticalJoins}: ${plan.verticalJoints}`);
    lines.push(`${this.t.leftEnd}: ${this.field('leftEnd').selectedOptions[0].textContent}; ${this.t.rightEnd}: ${this.field('rightEnd').selectedOptions[0].textContent}`);
    if(plan.band)lines.push(this.querySelector('[data-completion-info]').textContent);
    if(plan.mode==='mixed'||plan.spareSlats)lines.push(this.querySelector('[data-extra-info]').textContent);
    for(const product of [panel,slat].filter(p=>p.type==='panel'?plan.panels.quantity:plan.slats.quantity)){
      const modelURL=new URL(product.url,location.origin),variant=this.selectedVariants.get(product.handle)||product.variantId;
      if(variant)modelURL.searchParams.set('variant',variant);lines.push(`${this.t.coverage}: ${this.fmt(product.width)} ${this.t.cm}`,modelURL.href);
    }
    lines.push(`${this.t.kerfNote} ${this.fmt(plan.kerfMm)} ${this.t.mm}`,this.t.quoteCheck);
    const url=new URL('https://wa.me/972509445045');url.searchParams.set('text',lines.join('\n'));this.querySelector('[data-whatsapp]').href=url.href;
    this.querySelector('[data-announcement]').textContent=`${this.t.updated}: ${quantity.panels} ${this.t.panels}, ${quantity.slats} ${this.t.slats}, ${quantity.connectors} ${this.t.connectors}.`;
  }
}
if(!customElements.get('lora-quantity-planner'))customElements.define('lora-quantity-planner',LoraQuantityPlanner);
